import _ from "lodash";

import { AllowListType } from "@/PHIAllowListPage/types";
import { AccountMetadata, LocalStorageProject } from "@/reducers/environments";
import {
  LoadedDestinationInfo,
  PropertySensitivityLevels,
  SensitivityLevel,
} from "@/types/global";
import { isAccountInHIPAAModeV2 } from "@/util";
import destinationPropertyMetadataJSON from "./destinationPropertyMetadata.json";
import { DestinationKey } from "./types";

export enum PropertyGroup {
  Required = "required",
  RequiredWeb = "required_web",
  RequiredIOS = "required_ios",
  RequiredAndroid = "required_android",
  Recommended = "recommended",
  Attribution = "attribution",
  UTM = "utm",
  ViewThrough = "view-through",
  Optional = "optional",
}

// DestinationPropertyMetadata is metadata describing an event, user, or group
// property in the context of a particular destination. For example the "$gclid"
// property has particular significance for Google Ads.
export interface DestinationPropertyMetadata {
  property: string;
  // propertyGroup is used for presenting properties to customers in groups of
  // related properties. There is a group for required properties, a group for
  // recommended properties. There may be additional groups of recommended
  // properties that are functionally interrelated.
  propertyGroup: PropertyGroup;
  propertyType: AllowListType;
  description: string;
  // alternateName is used to support legacy names for required properties. The
  // destination is considered valid if either the property or the alternate
  // name are allowed.
  alternateName?: string;
  // grouping is used to indicate that this property belongs to a group.
  // Properties with the same grouping value are displayed together. If
  // groupingRequired is true (default), at least one property from the group
  // must be selected.
  grouping?: string;
  // groupingRequired indicates whether at least one property from the group
  // must be selected. Defaults to true for backward compatibility.
  groupingRequired?: boolean;
}

export interface AudienceIdentifier extends DestinationPropertyMetadata {
  label: string;
  usage: string[];
  usageLabel: string;
  unit: string;
  unitPlural: string;
}

export function destinationHasPropertyMetadata(
  destinationKey: DestinationKey,
): boolean {
  return Boolean(destinationPropertyMetadataJSON[destinationKey]);
}

/**
 * Helper to retrieve required identifiers for a destination/integration
 *
 * @param destinationKey destination.key or destination.name, typically a SaaSDestination.key or LoadedDestinationInfo.name
 * @param exclude identifier properties, use to filter out identifiers that are gated behind a feature flag
 * @returns {AudienceIdentifier[]} list of AudienceIdentifiers
 */
export function getRequiredPropertiesForDestination<
  T = DestinationPropertyMetadata[],
>(
  destinationKey: DestinationKey,
  exclude?: DestinationPropertyMetadata["property"][],
): T {
  const propertyMetadataList = destinationPropertyMetadataJSON[destinationKey];

  if (!propertyMetadataList) {
    console.warn(`could not find property metadata for: ${destinationKey}`);
    return [] as unknown as T;
  }

  const maybeFiltered = exclude?.length
    ? propertyMetadataList.filter(
        (identifier) => !exclude.includes(identifier.property),
      )
    : propertyMetadataList;

  // We trust the caller to specify T correctly
  return maybeFiltered as unknown as T;
}

// Returns all the property metadata in a given property group for a destination.
// Returns empty array if there are no property metadata for the destination.
export function getPropertyGroupMetadata(
  destinationKey: DestinationKey,
  propertyGroup: PropertyGroup,
): DestinationPropertyMetadata[] {
  const propertyMetadataList: DestinationPropertyMetadata[] =
    destinationPropertyMetadataJSON[destinationKey] || [];
  return _.filter(
    propertyMetadataList,
    (propertyMetadata) => propertyMetadata.propertyGroup === propertyGroup,
  );
}

export function getPropertySensitivityLevelsForDestination(
  destination: LoadedDestinationInfo,
  propertyType: AllowListType,
): PropertySensitivityLevels {
  switch (propertyType) {
    case AllowListType.Event:
      return destination.event_property_sensitivity_levels ?? {};
    case AllowListType.User:
      return destination.user_property_sensitivity_levels ?? {};
    case AllowListType.Group:
      return destination.group_property_sensitivity_levels ?? {};
  }
}

export function isPropertyAllowedForSensitivityLevels(
  propertyMetadata: DestinationPropertyMetadata,
  allowList: PropertySensitivityLevels,
): boolean {
  if (
    propertyMetadata.alternateName &&
    allowList[propertyMetadata.alternateName] === SensitivityLevel.NotSensitive
  ) {
    return true;
  }

  return allowList[propertyMetadata.property] === SensitivityLevel.NotSensitive;
}

function isPropertyMaskedForSensitivityLevels(
  propertyMetadata: DestinationPropertyMetadata,
  allowList: PropertySensitivityLevels,
): boolean {
  if (
    propertyMetadata.alternateName &&
    allowList[propertyMetadata.alternateName] ===
      SensitivityLevel.NotSensitiveIfHashed
  ) {
    return true;
  }

  return (
    allowList[propertyMetadata.property] ===
    SensitivityLevel.NotSensitiveIfHashed
  );
}

export function isPropertyAllowedForProject(
  propertyMetadata: DestinationPropertyMetadata,
  project: LocalStorageProject,
): boolean {
  let sensitivityLevels: PropertySensitivityLevels;
  switch (propertyMetadata.propertyType) {
    case AllowListType.Event:
      sensitivityLevels = project.eventPropertySensitivityLevels;
      break;
    case AllowListType.User:
      sensitivityLevels = project.userPropertySensitivityLevels;
      break;
    case AllowListType.Group:
      sensitivityLevels = project.groupPropertySensitivityLevels;
      break;
  }

  return isPropertyAllowedForSensitivityLevels(
    propertyMetadata,
    sensitivityLevels,
  );
}

export function isPropertyAllowedForDestination(
  propertyMetadata: DestinationPropertyMetadata,
  destination: LoadedDestinationInfo,
): boolean {
  return isPropertyAllowedForSensitivityLevels(
    propertyMetadata,
    getPropertySensitivityLevelsForDestination(
      destination,
      propertyMetadata.propertyType,
    ),
  );
}

export function isPropertyMaskedForDestination(
  propertyMetadata: DestinationPropertyMetadata,
  destination: LoadedDestinationInfo,
): boolean {
  return isPropertyMaskedForSensitivityLevels(
    propertyMetadata,
    getPropertySensitivityLevelsForDestination(
      destination,
      propertyMetadata.propertyType,
    ),
  );
}

// isPropertyAllowed returns true iff the property described by the given
// propertyMetadata is allowed for the given destination. It accounts for legacy
// alternate property names, and for if the property is on the project-level
// allowlist.
export function isPropertyAllowed(
  propertyMetadata: DestinationPropertyMetadata,
  project: LocalStorageProject,
  destination: LoadedDestinationInfo,
): boolean {
  return (
    isPropertyAllowedForProject(propertyMetadata, project) ||
    isPropertyAllowedForDestination(propertyMetadata, destination)
  );
}

// Separates properties into grouped (keyed by grouping name) and ungrouped.
// Exported so the HIPAA modal can reuse the same logic for rendering and
// validation without duplicating it.
export function groupPropertiesByGrouping(
  properties: DestinationPropertyMetadata[],
): {
  grouped: Record<string, DestinationPropertyMetadata[]>;
  ungrouped: DestinationPropertyMetadata[];
} {
  const grouped: Record<string, DestinationPropertyMetadata[]> = {};
  const ungrouped: DestinationPropertyMetadata[] = [];

  properties.forEach((prop) => {
    if (prop.grouping) {
      if (!grouped[prop.grouping]) {
        grouped[prop.grouping] = [];
      }
      grouped[prop.grouping].push(prop);
    } else {
      ungrouped.push(prop);
    }
  });

  return { grouped, ungrouped };
}

export function missingRequiredProperties(
  accountMetadata: AccountMetadata,
  project: LocalStorageProject,
  destination: LoadedDestinationInfo,
  exclude?: DestinationPropertyMetadata["property"][],
): DestinationPropertyMetadata[] {
  if (!isAccountInHIPAAModeV2(accountMetadata)) {
    return [];
  }

  if (destination.has_baa) {
    return [];
  }

  const destinationKey = destination.name;
  const propertyMetadataList = getPropertyGroupMetadata(
    destinationKey,
    PropertyGroup.Required,
  );

  const requiredProperties = exclude?.length
    ? propertyMetadataList.filter(
        (identifier) => !exclude.includes(identifier.property),
      )
    : propertyMetadataList;

  const { grouped, ungrouped } = groupPropertiesByGrouping(requiredProperties);

  const missing: DestinationPropertyMetadata[] = [];

  // For grouped properties: if the group is required and NO property in the
  // group is allowed, report all of them as missing (the user needs to allow
  // at least one). If at least one is allowed, the group is satisfied and
  // none are reported as missing.
  for (const groupName in grouped) {
    const groupProps = grouped[groupName];
    const isRequired = groupProps.some(
      (prop) => prop.groupingRequired !== false,
    );
    if (!isRequired) {
      continue;
    }
    const hasAllowed = groupProps.some((prop) =>
      isPropertyAllowed(prop, project, destination),
    );
    if (!hasAllowed) {
      missing.push(...groupProps);
    }
  }

  // For ungrouped properties, each must be individually allowed.
  ungrouped.forEach((p) => {
    if (!isPropertyAllowed(p, project, destination)) {
      missing.push(p);
    }
  });

  return missing;
}

export function areAllRequiredPropertiesAllowed(
  accountMetadata: AccountMetadata,
  project: LocalStorageProject,
  destination: LoadedDestinationInfo,
  exclude?: DestinationPropertyMetadata["property"][],
): boolean {
  // Per-destination allowlisting is only available in HIPAA mode V2. If HIPAA
  // Mode V1, or if not in HIPAA mode at all, we can say that all required
  // properties are allowed.
  if (!isAccountInHIPAAModeV2(accountMetadata)) {
    return true;
  }

  if (destination.has_baa) {
    return true;
  }

  const destinationKey = destination.name;
  const propertyMetadataList = getPropertyGroupMetadata(
    destinationKey,
    PropertyGroup.Required,
  );

  const maybeFiltered = exclude?.length
    ? propertyMetadataList.filter(
        (identifier) => !exclude.includes(identifier.property),
      )
    : propertyMetadataList;

  const { grouped, ungrouped } = groupPropertiesByGrouping(maybeFiltered);

  // For grouped properties, at least one must be allowed if the group is
  // required (groupingRequired defaults to true).
  for (const groupName in grouped) {
    const groupProps = grouped[groupName];
    const isRequired = groupProps.some(
      (prop) => prop.groupingRequired !== false,
    );
    if (isRequired) {
      const hasAllowed = groupProps.some((prop) =>
        isPropertyAllowed(prop, project, destination),
      );
      if (!hasAllowed) {
        return false;
      }
    }
  }

  // For ungrouped properties, every one must be allowed.
  return _.every(ungrouped, (propertyMetadata) =>
    isPropertyAllowed(propertyMetadata, project, destination),
  );
}
